/** @format */

import React, { useRef } from "react";
import {
  ChevronDown,
  Phone,
  PhoneCall,
  ShoppingCart,
  Home,
  Palette,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import { CategoryInterface } from "@/types/category";
import { MegaMenu } from "./MegaMenu";
import { StoreInterface } from "@/types/store";

interface NavbarNavigationProps {
  categories: CategoryInterface[];
  activeMegaMenu: number | null;
  setActiveMegaMenu: (id: number | null) => void;
  hoveredParentCategory: number | null;
  setHoveredParentCategory: (id: number | null) => void;
  cartQuantity: number;
  storeInfo: StoreInterface;
  className?: string;
}

export const NavbarNavigation: React.FC<NavbarNavigationProps> = ({
  categories,
  activeMegaMenu,
  setActiveMegaMenu,
  hoveredParentCategory,
  cartQuantity,
  storeInfo,
  setHoveredParentCategory,
  className,
}) => {
  const categoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getCategoryDisplayName = (category: CategoryInterface) => {
    return category.name;
  };

  const hasMegaMenu = (category: CategoryInterface) => {
    return category.subCategories && category.subCategories.length > 0;
  };

  const handleMegaMenuHover = (categoryId: number) => {
    if (categoryTimeoutRef.current) {
      clearTimeout(categoryTimeoutRef.current);
    }
    setActiveMegaMenu(categoryId);
    const category = categories.find((cat) => cat.id === categoryId);
    if (category && category.subCategories.length > 0) {
      setHoveredParentCategory(category.subCategories[0].id);
    }
  };

  const handleMegaMenuLeave = () => {
    categoryTimeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
      setHoveredParentCategory(null);
    }, 150);
  };

  const handleMenuEnter = () => {
    if (categoryTimeoutRef.current) {
      clearTimeout(categoryTimeoutRef.current);
    }
  };

  const handleMenuLeave = () => {
    setActiveMegaMenu(null);
    setHoveredParentCategory(null);
  };

  const getCategoryIcon = (slug: string) => {
    switch (slug) {
      case "danh-muc-san-pham":
        return Home;
      case "thiet-ke":
        return Palette;
      default:
        return Sparkles;
    }
  };

  return (
    <div
      className={`hidden lg:block border-b border-border bg-background backdrop-blur-sm ${className}`}>
      <div className="container mx-auto py-2">
        <div className="flex items-center justify-between">
          {/* ================= MAIN NAVIGATION ================= */}
          <nav className="flex items-center space-x-2 flex-1">
            {categories.map((category) => {
              const IconComponent = getCategoryIcon(category.slug);

              return (
                <div
                  key={category.id}
                  className="relative"
                  onMouseEnter={() =>
                    hasMegaMenu(category) && handleMegaMenuHover(category.id)
                  }
                  onMouseLeave={handleMegaMenuLeave}>
                  {hasMegaMenu(category) ? (
                    <button
                      className="
                      group relative flex items-center gap-2
                      px-3 py-2 rounded-lg
                      text-sm font-medium
                      text-foreground
                      border border-transparent
                      transition-all duration-300
                      hover:bg-muted
                      hover:border-border
                      hover:text-primary
                      hover:shadow-sm
                    ">
                      <IconComponent
                        size={18}
                        className="transition-transform duration-300 group-hover:scale-110"
                      />
                      <span>{getCategoryDisplayName(category)}</span>
                      <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
                    </button>
                  ) : (
                    <Link
                      prefetch
                      href={`/danh-muc/${category.slug}`}
                      className="
                      group relative flex items-center gap-2
                      px-3 py-2 rounded-lg
                      text-sm font-medium
                      text-foreground
                      border border-transparent
                      transition-all duration-300
                      hover:bg-muted
                      hover:border-border
                      hover:text-primary
                      hover:shadow-sm
                    ">
                      <IconComponent
                        size={18}
                        className="transition-transform duration-300 group-hover:scale-110"
                      />
                      <span>{getCategoryDisplayName(category)}</span>
                    </Link>
                  )}

                  {hasMegaMenu(category) && activeMegaMenu === category.id && (
                    <MegaMenu
                      category={category}
                      hoveredParentCategory={hoveredParentCategory}
                      onParentCategoryHover={setHoveredParentCategory}
                      onMenuEnter={handleMenuEnter}
                      onMenuLeave={handleMenuLeave}
                    />
                  )}
                </div>
              );
            })}

            {/* ===== STATIC LINKS ===== */}
            {[
              { href: "/gioi-thieu", label: "Về chúng tôi" },
              { href: "/lien-he", label: "Liên hệ" },
              { href: "/hop-am-guitar", label: "Hợp âm guitar" },
              { href: "/metronome", label: "Metronome" },
              { href: "/guitar-nhat-bai", label: "Guitar Nhật Bãi" },
            ].map((item) => (
              <Link
                key={item.href}
                prefetch
                href={item.href}
                className="
                group relative flex items-center
                px-1 py-2 rounded-lg
                text-sm font-medium
                text-foreground
                border border-transparent
                transition-all duration-300
                hover:bg-muted
                hover:border-border
                hover:text-primary
                hover:shadow-sm
               
              ">
                {item.label}
              </Link>
            ))}
          </nav>

          {/* ================= ACTION BUTTONS ================= */}
          <div className="flex items-center gap-3">
            {/* Hotline */}
            {/* <Link
              href={`tel:${storeInfo.phone}`}
              prefetch
              className="
              group relative flex items-center gap-3
              px-4 py-2 rounded-xl
              bg-background
              border border-border
              text-foreground
              shadow-sm
              transition-all duration-300
              hover:bg-green-50
              hover:border-green-500
              hover:text-green-600
              hover:-translate-y-1
              hover:shadow-md
            ">
              <PhoneCall className="w-5 h-5 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" />

              <div className="hidden xl:flex flex-col">
                <span className="text-xs opacity-70 leading-none">Hotline</span>
                <span className="text-sm font-semibold leading-tight">
                  {storeInfo.phone ?? ""}
                </span>
              </div>

              <span className="xl:hidden text-sm font-medium">Gọi ngay</span>
            </Link> */}

            {/* Cart */}
            <Link
              href="/gio-hang"
              prefetch
              className="
              group relative flex items-center gap-3
              px-4 py-2 rounded-xl
              bg-primary
              border border-primary
              text-primary-foreground
              shadow-sm
              transition-all duration-300
              hover:opacity-95
              hover:-translate-y-1
              hover:scale-[1.02]
              hover:shadow-md
            ">
              <ShoppingCart className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />

              <div className="hidden lg:flex flex-col items-center">
                <span className="text-xs opacity-90 leading-none">
                  Giỏ hàng
                </span>
                <span className="text-sm font-semibold leading-tight">
                  {cartQuantity > 0 ? `(${cartQuantity})` : "(0)"}
                </span>
              </div>

              <span className="lg:hidden text-sm font-semibold">Giỏ hàng</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
